import { IgGrayPrimitives } from "@intelligems/ig-design-tokens/constants";
import type {
  ComponentType,
  ModInsertType,
} from "@intelligems/ig-types/schemas/visual-builder/mod-schemas";
import { bundleOnsiteUpsellModHandler } from "@src/store/slices/experienceBuilder/component/utils/bundleOnsiteUpsellModHandler";
import { onsiteUpsellModHandler } from "@src/store/slices/experienceBuilder/component/utils/onsiteUpsellModHandler";
import type {
  BannerComponent,
  BundleOnsiteUpsellComponent,
  GenericComponent,
  ImageComponent,
  OnsiteUpsellComponent,
  TextComponent,
} from "@src/store/slices/experienceBuilder/types";

interface DefaultContext {
  id: string;
  variationId: string;
  selector: string;
  insertType: ModInsertType;
  createdAtUrl?: string;
}

const baseFields = (ctx: DefaultContext) => ({
  id: ctx.id,
  category: "component" as const,
  variationId: ctx.variationId,
  selector: ctx.selector,
  insertType: ctx.insertType,
  createdAtUrl: ctx.createdAtUrl,
});

export function getDefaultText(ctx: DefaultContext): TextComponent {
  return {
    ...baseFields(ctx),
    type: "textComponent",
    content: "",
    lineHeight: "1.5",
    letterSpacing: "0",
    padding: "0",
    margin: "0",
  };
}

export function getDefaultImage(ctx: DefaultContext): ImageComponent {
  return {
    ...baseFields(ctx),
    type: "imageComponent",
    src: "",
    alt: "",
    width: "100%",
    maxHeight: "",
    scaling: "contain",
    objectPosition: "center",
  };
}

export function getDefaultBanner(ctx: DefaultContext): BannerComponent {
  return {
    ...baseFields(ctx),
    type: "bannerComponent",
    content: "",
    iconPosition: "left",
    backgroundColor: IgGrayPrimitives[400],
    strokeColor: IgGrayPrimitives[800],
    strokeWidth: "1",
    cornerRadius: "8",
    padding: "16",
    margin: "0",
    lineHeight: "1.5",
    letterSpacing: "0",
  };
}

export function getDefaultGenericComponent(
  ctx: DefaultContext
): GenericComponent {
  return {
    ...baseFields(ctx),
    type: "genericComponent",
    definition: "",
  };
}

// Required only to satisfy the exhaustive `Record<ComponentType, …>` now that
// `onsiteUpsell` is a ComponentType — no creation flow reaches it in this
// ticket. `replace` isn't a valid upsell placement, so it's coerced to
// `afterend`. TODO: exclude `onsiteUpsell` from the generic component picker
// once its own authoring flow lands.
export function getDefaultOnsiteUpsell(
  ctx: DefaultContext
): OnsiteUpsellComponent {
  return {
    ...onsiteUpsellModHandler.defaults(),
    id: ctx.id,
    variationId: ctx.variationId,
    selector: ctx.selector,
    insertType: ctx.insertType === "replace" ? "afterend" : ctx.insertType,
    createdAtUrl: ctx.createdAtUrl,
  };
}

export function getDefaultBundleOnsiteUpsell(
  ctx: DefaultContext
): BundleOnsiteUpsellComponent {
  return {
    ...bundleOnsiteUpsellModHandler.defaults(),
    id: ctx.id,
    variationId: ctx.variationId,
    selector: ctx.selector,
    insertType: ctx.insertType === "replace" ? "afterend" : ctx.insertType,
    createdAtUrl: ctx.createdAtUrl,
  };
}

export const componentDefaults: Record<
  ComponentType,
  (
    ctx: DefaultContext
  ) =>
    | TextComponent
    | ImageComponent
    | BannerComponent
    | GenericComponent
    | OnsiteUpsellComponent
    | BundleOnsiteUpsellComponent
> = {
  textComponent: getDefaultText,
  imageComponent: getDefaultImage,
  bannerComponent: getDefaultBanner,
  genericComponent: getDefaultGenericComponent,
  onsiteUpsell: getDefaultOnsiteUpsell,
  bundleOnsiteUpsell: getDefaultBundleOnsiteUpsell,
};
